import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Plus, Search, Pencil, Trash2, LayoutGrid, LayoutList, X, ArrowUpDown, CheckCircle2, Circle, Download, Upload, PencilLine, MoreVertical, SlidersHorizontal } from "lucide-react";
import type { MenuItemSortDir, MenuItemSortKey } from "@/types/menu";
import { useDietaryTags, getDietaryTagLabel } from "@/hooks/useDietaryTags";
import { useState, useMemo, useRef } from "react";
import { useMenuItems, useDeleteMenuItem, useUpdateMenuItem, useBulkUpdateMenuItems, useBulkDeleteMenuItems, useBulkUpdateDietaryTags, type MenuItemRow } from "@/hooks/useMenuItems";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useCategories } from "@/hooks/useCategories";
import { MenuItemFormDialog } from "@/components/MenuItemFormDialog";
import { MenuItemImportDialog } from "@/components/MenuItemImportDialog";
import { MenuItemMobileList, MenuItemPrices } from "@/components/MenuItemMobileList";
import { CategoryManagementSheet } from "@/components/CategoryManagementSheet";
import { DietaryTagManagementSheet } from "@/components/DietaryTagManagementSheet";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

type StatusFilter = "all" | "active" | "inactive";
type ViewMode = "table" | "grid";

const MenuItemsPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState<MenuItemSortKey>("name");
  const [sortDir, setSortDir] = useState<MenuItemSortDir>("asc");
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState("");
  const [bulkTagMode, setBulkTagMode] = useState<"add" | "remove" | null>(null);
  const [bulkTagSelection, setBulkTagSelection] = useState<string[]>([]);
  const [inlineEdit, setInlineEdit] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const { data: items, isLoading } = useMenuItems();
  const { data: categories } = useCategories();
  const deleteItem = useDeleteMenuItem();
  const updateItem = useUpdateMenuItem();
  const bulkUpdate = useBulkUpdateMenuItems();
  const bulkDelete = useBulkDeleteMenuItems();
  const bulkTagUpdate = useBulkUpdateDietaryTags();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MenuItemRow | null>(null);
  const [categorySheetOpen, setCategorySheetOpen] = useState(false);
  const [tagSheetOpen, setTagSheetOpen] = useState(false);
  const { data: dietaryTags } = useDietaryTags();

  const itemCountsByCategory = useMemo(() => {
    if (!items) return {};
    const counts: Record<string, number> = {};
    items.forEach((i) => { counts[i.category_id] = (counts[i.category_id] ?? 0) + 1; });
    return counts;
  }, [items]);

  const hasFilters = searchQuery || categoryFilter !== "all" || tagFilters.length > 0 || statusFilter !== "all";

  const filtered = useMemo(() => {
    if (!items) return [];
    let result = items;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((i) => i.name.toLowerCase().includes(q) || i.category?.name?.toLowerCase().includes(q) || i.sku?.toLowerCase().includes(q));
    }
    if (categoryFilter !== "all") {
      result = result.filter((i) => i.category_id === categoryFilter);
    }
    if (tagFilters.length > 0) {
      result = result.filter((i) => tagFilters.some((t) => i.dietary_tags?.includes(t)));
    }
    if (statusFilter === "active") result = result.filter((i) => i.is_active);
    if (statusFilter === "inactive") result = result.filter((i) => !i.is_active);

    result = [...result].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "name") cmp = a.name.localeCompare(b.name);
      else if (sortKey === "category") cmp = (a.category?.name ?? "").localeCompare(b.category?.name ?? "");
      else if (sortKey === "price") {
        const ap = a.prices?.[0]?.price ?? 0;
        const bp = b.prices?.[0]?.price ?? 0;
        cmp = ap - bp;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

    return result;
  }, [items, searchQuery, categoryFilter, tagFilters, statusFilter, sortKey, sortDir]);

  const toggleSort = (key: MenuItemSortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const setSort = (key: MenuItemSortKey, dir: MenuItemSortDir) => { setSortKey(key); setSortDir(dir); };

  const toggleTag = (tag: string) => setTagFilters((t) => (t.includes(tag) ? t.filter((x) => x !== tag) : [...t, tag]));
  const clearFilters = () => { setSearchQuery(""); setCategoryFilter("all"); setTagFilters([]); setStatusFilter("all"); };

  const toggleSelect = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((i) => i.id)));
  };
  const selectedIds = Array.from(selected);

  const openEdit = (item: MenuItemRow) => { setEditing(item); setFormOpen(true); };
  const openNew = () => { setEditing(null); setFormOpen(true); };

  const handleBulkActivate = (active: boolean) => {
    bulkUpdate.mutate({ ids: selectedIds, updates: { is_active: active } }, { onSuccess: () => setSelected(new Set()) });
  };
  const handleBulkDelete = () => {
    bulkDelete.mutate(selectedIds, { onSuccess: () => { setSelected(new Set()); setBulkDeleteOpen(false); } });
  };
  const handleBulkCategory = () => {
    if (!bulkCategoryId) return;
    bulkUpdate.mutate({ ids: selectedIds, updates: { category_id: bulkCategoryId } }, { onSuccess: () => { setSelected(new Set()); setBulkCategoryOpen(false); } });
  };
  const openBulkTagDialog = (mode: "add" | "remove") => { setBulkTagMode(mode); setBulkTagSelection([]); };
  const toggleBulkTag = (tag: string) => setBulkTagSelection((t) => t.includes(tag) ? t.filter((x) => x !== tag) : [...t, tag]);
  const handleBulkTags = () => {
    if (!bulkTagSelection.length || !items) return;
    const selectedItems = items.filter((i) => selected.has(i.id));
    const payload = bulkTagMode === "add"
      ? { items: selectedItems, addTags: bulkTagSelection }
      : { items: selectedItems, removeTags: bulkTagSelection };
    bulkTagUpdate.mutate(payload, { onSuccess: () => { setSelected(new Set()); setBulkTagMode(null); } });
  };

  // CSV Export
  const handleExportCSV = () => {
    const escapeCSV = (val: string) => {
      if (val.includes(",") || val.includes('"') || val.includes("\n")) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const header = ["Name", "Description", "Category", "Pricing Type", "Price", "Label", "Serves", "SKU", "Abbreviation", "Dietary Tags", "Active"];
    const rows = filtered.map((item) => {
      const price = item.prices?.[0];
      return [
        escapeCSV(item.name),
        escapeCSV(item.description ?? ""),
        escapeCSV(item.category?.name ?? ""),
        escapeCSV(price?.pricing_type ?? ""),
        price ? String(price.price) : "",
        escapeCSV(price?.label ?? ""),
        price?.serves ? String(price.serves) : "",
        escapeCSV(item.sku ?? ""),
        escapeCSV(item.abbreviation ?? ""),
        escapeCSV((item.dietary_tags ?? []).join(", ")),
        item.is_active ? "true" : "false",
      ].join(",");
    });

    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `menu-items-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Inline edit handler
  const handleInlineUpdate = (id: string, field: string, value: string | null) => {
    updateItem.mutate({ id, [field]: value });
  };

  const itemCount = items?.length ?? 0;
  const shownCount = filtered.length;

  return (
    <DashboardLayout title="Menu Items">
      <div className="space-y-4">
        {/* Row 1: Title + Primary CTA */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-sm">
              Manage your catering menu items, pricing, and dietary information.
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {hasFilters ? `${shownCount} of ${itemCount} items` : `${itemCount} items`}
            </p>
          </div>
          <Button onClick={openNew}>
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        </div>

        {/* Row 2: Search + Filters + Actions overflow */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search name, category, SKU..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
          </div>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories?.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} ({itemCountsByCategory[c.id] ?? 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active Only</SelectItem>
              <SelectItem value="inactive">Inactive Only</SelectItem>
            </SelectContent>
          </Select>

          {/* Dietary tag filter popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Tags
                {tagFilters.length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 text-[10px] flex items-center justify-center">
                    {tagFilters.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-3" align="start">
              <p className="text-xs font-medium text-muted-foreground mb-2">Filter by dietary tags</p>
              <div className="flex flex-wrap gap-1.5">
                {dietaryTags?.map((tag) => (
                  <Badge
                    key={tag.key}
                    variant={tagFilters.includes(tag.key) ? "default" : "outline"}
                    className="cursor-pointer text-xs"
                    onClick={() => toggleTag(tag.key)}
                  >
                    {tag.label}
                  </Badge>
                ))}
              </div>
              {tagFilters.length > 0 && (
                <Button variant="ghost" size="sm" className="w-full mt-2 text-xs h-7" onClick={() => setTagFilters([])}>
                  Clear tag filters
                </Button>
              )}
            </PopoverContent>
          </Popover>

          {/* Actions overflow menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" aria-label="More actions">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleExportCSV} disabled={!filtered.length}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setImportDialogOpen(true)}>
                <Upload className="h-4 w-4 mr-2" /> Import CSV
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {/* No inline-edit layout on the mobile card list (#693). */}
              <DropdownMenuItem className="hidden md:flex" onClick={() => setInlineEdit(!inlineEdit)}>
                <PencilLine className="h-4 w-4 mr-2" />
                {inlineEdit ? "Exit Inline Edit" : "Inline Edit"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setViewMode(viewMode === "table" ? "grid" : "table")}>
                {viewMode === "table" ? <LayoutGrid className="h-4 w-4 mr-2" /> : <LayoutList className="h-4 w-4 mr-2" />}
                {viewMode === "table" ? "Grid View" : "Table View"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setCategorySheetOpen(true)}>
                Manage Categories
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTagSheetOpen(true)}>
                Manage Dietary Tags
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs gap-1">
              <X className="h-3 w-3" /> Clear all
            </Button>
          )}
        </div>

        {/* Active filter chips — only shown when filters are applied */}
        {tagFilters.length > 0 && (
          <div className="flex flex-wrap gap-1.5 items-center">
            {tagFilters.map((key) => (
              <Badge key={key} variant="secondary" className="gap-1 pr-1 text-xs">
                {getDietaryTagLabel(dietaryTags, key)}
                <button onClick={() => toggleTag(key)} className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 bg-muted/50 border rounded-lg px-4 py-2">
            <span className="text-sm font-medium">{selected.size} selected</span>
            <Button variant="outline" size="sm" onClick={() => handleBulkActivate(true)}>Activate</Button>
            <Button variant="outline" size="sm" onClick={() => handleBulkActivate(false)}>Deactivate</Button>
            <Button variant="outline" size="sm" onClick={() => setBulkCategoryOpen(true)}>Change Category</Button>
            <Button variant="outline" size="sm" onClick={() => openBulkTagDialog("add")}>Add Tags</Button>
            <Button variant="outline" size="sm" onClick={() => openBulkTagDialog("remove")}>Remove Tags</Button>
            <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)}>Delete</Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              <X className="h-3 w-3 mr-1" /> Deselect
            </Button>
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : !filtered.length ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Plus className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="font-display text-lg font-semibold mb-1">{hasFilters ? "No matching items" : "No menu items yet"}</h3>
              <p className="text-muted-foreground text-sm max-w-md">
                {hasFilters ? "Try adjusting your filters." : "Start building your menu by adding items with pricing, dietary tags, and descriptions."}
              </p>
              {!hasFilters && (
                <Button className="mt-4" onClick={openNew}>
                  <Plus className="h-4 w-4 mr-2" /> Add Your First Item
                </Button>
              )}
            </CardContent>
          </Card>
        ) : viewMode === "table" ? (
          <>
          {/* md+ only; below 768px the card list replaces the table (#693). */}
          <Card className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={selected.size === filtered.length && filtered.length > 0} onCheckedChange={toggleAll} />
                  </TableHead>
                  <TableHead className="w-12"></TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("name")}>
                    Name <ArrowUpDown className="inline h-3 w-3 ml-1" />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("category")}>
                    Category <ArrowUpDown className="inline h-3 w-3 ml-1" />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("price")}>
                    Pricing <ArrowUpDown className="inline h-3 w-3 ml-1" />
                  </TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead className="w-12">
                    <span className="flex items-center gap-1" title="Square Sync Status">
                      <svg className="h-3.5 w-3.5 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="3" width="18" height="18" rx="3"/></svg>
                    </span>
                  </TableHead>
                  <TableHead className="w-16">Active</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((item) => (
                  <TableRow key={item.id} className={`${selected.has(item.id) ? "bg-muted/30" : ""} ${inlineEdit ? "bg-accent/5" : ""}`}>
                    <TableCell>
                      <Checkbox checked={selected.has(item.id)} onCheckedChange={() => toggleSelect(item.id)} />
                    </TableCell>
                    <TableCell>
                      <Avatar className="h-8 w-8">
                        {item.image_url && <AvatarImage src={item.image_url} alt={item.name} />}
                        <AvatarFallback className="text-xs">{item.name.charAt(0)}</AvatarFallback>
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      {inlineEdit ? (
                        <div className="space-y-1">
                          <InlineInput
                            value={item.name}
                            onSave={(v) => handleInlineUpdate(item.id, "name", v)}
                            className="font-medium"
                          />
                          <InlineInput
                            value={item.description ?? ""}
                            onSave={(v) => handleInlineUpdate(item.id, "description", v || null)}
                            placeholder="Description..."
                            className="text-xs text-muted-foreground"
                          />
                        </div>
                      ) : (
                        <div>
                          <p className="font-medium">{item.name}</p>
                          {item.sku && <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>}
                          {item.description && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{item.description}</p>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {inlineEdit ? (
                        <Select
                          value={item.category_id}
                          onValueChange={(v) => handleInlineUpdate(item.id, "category_id", v)}
                        >
                          <SelectTrigger className="h-8 text-sm w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories?.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-sm">{item.category?.name ?? "—"}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {inlineEdit && item.prices?.[0] ? (
                        <InlineInput
                          value={String(Number(item.prices[0].price).toFixed(2))}
                          onSave={(v) => {
                            const price = parseFloat(v);
                            if (!isNaN(price)) {
                              updateItem.mutate({
                                id: item.id,
                                prices: item.prices?.map((p, idx) =>
                                  idx === 0
                                    ? { pricing_type: p.pricing_type, price, label: p.label ?? undefined, serves: p.serves ?? undefined, sku: p.sku ?? undefined }
                                    : { pricing_type: p.pricing_type, price: p.price, label: p.label ?? undefined, serves: p.serves ?? undefined, sku: p.sku ?? undefined }
                                ) ?? [],
                              });
                            }
                          }}
                          prefix="$"
                          className="text-sm w-20"
                        />
                      ) : (
                        <MenuItemPrices item={item} />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {inlineEdit ? (
                          dietaryTags?.map((dt) => {
                            const isOn = item.dietary_tags?.includes(dt.key);
                            return (
                              <Badge
                                key={dt.key}
                                variant={isOn ? "default" : "outline"}
                                className="text-xs cursor-pointer select-none"
                                onClick={() => {
                                  const current = item.dietary_tags ?? [];
                                  const updated = isOn
                                    ? current.filter((t) => t !== dt.key)
                                    : [...current, dt.key];
                                  updateItem.mutate({ id: item.id, dietary_tags: updated });
                                }}
                              >
                                {dt.label}
                              </Badge>
                            );
                          })
                        ) : (
                          item.dietary_tags?.map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {getDietaryTagLabel(dietaryTags, tag)}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span title={item.square_catalog_id ? `Synced to Square: ${item.square_catalog_id}` : "Not synced to Square"}>
                        {item.square_catalog_id ? (
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                        ) : (
                          <Circle className="h-4 w-4 text-muted-foreground/40" />
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Switch checked={item.is_active} onCheckedChange={(checked) => updateItem.mutate({ id: item.id, is_active: checked })} />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(item)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{item.name}"?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteItem.mutate(item.id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <MenuItemMobileList
            className="md:hidden"
            items={filtered}
            selected={selected}
            dietaryTags={dietaryTags}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={setSort}
            onToggleSelect={toggleSelect}
            onToggleAll={toggleAll}
            onToggleActive={(id, active) => updateItem.mutate({ id, is_active: active })}
            onEdit={openEdit}
            onDelete={(id) => deleteItem.mutate(id)}
          />
          </>
        ) : (
          /* Grid view */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((item) => (
              <Card key={item.id} className={`overflow-hidden ${selected.has(item.id) ? "ring-2 ring-primary" : ""}`}>
                <div className="relative aspect-[4/3] bg-muted">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-4xl font-bold">
                      {item.name.charAt(0)}
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    <Checkbox checked={selected.has(item.id)} onCheckedChange={() => toggleSelect(item.id)} className="bg-background/80" />
                  </div>
                  {item.square_catalog_id && (
                    <div className="absolute top-2 right-2" title="Synced to Square">
                      <CheckCircle2 className="h-4 w-4 text-primary drop-shadow" />
                    </div>
                  )}
                </div>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-sm leading-tight">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.category?.name}</p>
                    </div>
                    <Badge variant={item.is_active ? "default" : "secondary"} className="text-xs shrink-0">
                      {item.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  {item.prices && item.prices.length > 0 && (
                    <p className="text-sm font-medium">${Number(item.prices[0].price).toFixed(2)} {item.prices[0].label ? `(${item.prices[0].label})` : ""}</p>
                  )}
                  {item.dietary_tags && item.dietary_tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {item.dietary_tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-[10px] py-0">{getDietaryTagLabel(dietaryTags, tag)}</Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-1 pt-1">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(item)}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm"><Trash2 className="h-3 w-3 text-destructive" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete "{item.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteItem.mutate(item.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Bulk delete dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} items?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete}>Delete All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk category dialog */}
      <AlertDialog open={bulkCategoryOpen} onOpenChange={setBulkCategoryOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change category for {selected.size} items</AlertDialogTitle>
          </AlertDialogHeader>
          <Select value={bulkCategoryId} onValueChange={setBulkCategoryId}>
            <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              {categories?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkCategory} disabled={!bulkCategoryId}>Apply</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk tag dialog */}
      <Dialog open={bulkTagMode !== null} onOpenChange={(open) => { if (!open) setBulkTagMode(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{bulkTagMode === "add" ? "Add" : "Remove"} dietary tags for {selected.size} items</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 py-4">
            {dietaryTags?.map((tag) => (
              <Badge
                key={tag.key}
                variant={bulkTagSelection.includes(tag.key) ? "default" : "outline"}
                className="cursor-pointer text-sm px-3 py-1.5"
                onClick={() => toggleBulkTag(tag.key)}
              >
                {tag.label}
              </Badge>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkTagMode(null)}>Cancel</Button>
            <Button onClick={handleBulkTags} disabled={!bulkTagSelection.length || bulkTagUpdate.isPending}>
              {bulkTagUpdate.isPending ? "Updating..." : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MenuItemFormDialog key={editing?.id ?? "new"} open={formOpen} onOpenChange={setFormOpen} item={editing} />
      <MenuItemImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        existingItems={items ?? []}
        categories={categories?.map((c) => ({ id: c.id, name: c.name })) ?? []}
      />
      <CategoryManagementSheet open={categorySheetOpen} onOpenChange={setCategorySheetOpen} itemCountsByCategory={itemCountsByCategory} />
      <DietaryTagManagementSheet open={tagSheetOpen} onOpenChange={setTagSheetOpen} />
    </DashboardLayout>
  );
};

// Inline editable text input component
function InlineInput({
  value,
  onSave,
  placeholder,
  className,
  prefix,
}: {
  value: string;
  onSave: (value: string) => void;
  placeholder?: string;
  className?: string;
  prefix?: string;
}) {
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleBlur = () => {
    if (localValue !== value) {
      onSave(localValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      inputRef.current?.blur();
    } else if (e.key === "Escape") {
      setLocalValue(value);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="flex items-center gap-1">
      {prefix && <span className="text-sm text-muted-foreground">{prefix}</span>}
      <input
        ref={inputRef}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`bg-transparent border-b border-dashed border-muted-foreground/30 focus:border-primary focus:outline-none px-0.5 py-0 ${className ?? ""}`}
      />
    </div>
  );
}

export default MenuItemsPage;
